import { CSSProperties, useCallback, useEffect, useRef, useState } from 'react'

export const useAvailableHeight = () => {
  const nodeRef = useRef<HTMLElement | null>(null)
  const parentRectRef = useRef<{ top: number; left: number; width: number } | null>(null)
  const [style, setStyle] = useState<CSSProperties>({
    position: 'fixed',
    visibility: 'hidden',
  })

  const recompute = useCallback(() => {
    const parent = nodeRef.current?.parentElement
    if (!parent) return

    const rect = parent.getBoundingClientRect()
    if (rect.width > 0) {
      parentRectRef.current = { top: rect.top, left: rect.left, width: rect.width }
    }
    const cached = parentRectRef.current ?? { top: 0, left: 0, width: window.innerWidth }

    setStyle({
      position: 'fixed',
      top: cached.top,
      left: cached.left,
      width: cached.width,
      height: Math.max(0, window.innerHeight - cached.top),
      visibility: 'visible',
    })
  }, [])

  const ref = useCallback(
    (node: HTMLElement | null) => {
      nodeRef.current = node
      if (node) recompute()
    },
    [recompute],
  )

  useEffect(() => {
    window.addEventListener('resize', recompute)

    let resizeObserver: ResizeObserver | undefined
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(recompute)
      const parent = nodeRef.current?.parentElement
      if (parent) resizeObserver.observe(parent)
    }

    return () => {
      window.removeEventListener('resize', recompute)
      resizeObserver?.disconnect()
    }
  }, [recompute])

  return { ref, style }
}
